/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential.
 *
 * INVITE WITH DOCUMENTS: THE CHECKLIST SAYS WHAT THE FIRM ALREADY HOLDS
 * (owner 15/10/2026). Production Test2: the firm supplied "01 Certificate of
 * incorporation.pdf", the reading worked, and the client's checklist still
 * asked for "Certificate of Incorporation". The checklist is built from the
 * pre screening answers and the firm's checklist settings only.
 *
 * The server decides which documents count (api/_doc-access.js
 * clientDocumentList): a document the firm supplied, never an internal record,
 * not replaced by the client, not one that needs a current copy, and only once
 * its reading worked. It sends the client the document type the reading named
 * (provided_as), never the reading itself. This module matches checklist items
 * to those documents, for the page and for the emailed list alike.
 */

// A reading that worked: a real analysis with a document type, never the stub
// a failed reading leaves (the same test as df-process isReadStub) and never
// a document the reader could not open.
export function readingWorked(a) {
  if (!a || typeof a !== 'object' || !a.docType) return false;
  if (a._analysisError) return false;
  if (a.verdict === 'unreadable' || a.unreadable === true) return false;
  if (/analysis failed/i.test(String(a.summary || ''))) return false;
  return !(Array.isArray(a.keyFindings) ? a.keyFindings : []).some(k => /^\s*Analysis failed\b/i.test(String(k)));
}

// The specific kind of document a label or a reading names. Narrow on purpose:
// a certificate of incorporation never covers the articles, a register of
// members never covers the register of directors.
const KINDS = [
  ['certificate_of_incorporation', /certificate of incorporation|incorporation certificate|certificate of registration of (a )?company/i],
  ['articles', /memorandum|articles of association|\bM&A\b|constitution|bylaws|statutes/i],
  ['register_of_members', /register of (members|shareholders)|shareholder register|share register/i],
  ['register_of_directors', /register of (directors|officers)|directors'? register/i],
  ['psc_register', /\bPSC\b|persons? with significant control/i],
  ['structure_chart', /structure chart|group structure|ownership (chart|structure diagram)/i],
  ['bank_statement', /bank statement|statement of account/i],
  ['proof_of_address', /proof of (business |registered |trading )?address|utility bill/i],
  ['accounts', /(audited|annual|statutory|management|filed) accounts|financial statements/i],
  ['good_standing', /good standing|certificate of incumbency|incumbency/i],
  ['licence', /licen[cs]e|regulatory authori[sz]ation|registration certificate/i],
  ['passport', /passport|identity (document|card)|photo id/i],
];
export function kindOf(text) {
  const t = String(text || '');
  for (const [k, re] of KINDS) if (re.test(t)) return k;
  return null;
}

/**
 * For each checklist item, the firm supplied document that covers it (or null).
 * docs: the client's document list rows ({ name, providedAs }); only rows with
 * providedAs (set by the server, see above) can cover anything.
 * Returns [{ item, providedBy }] in the checklist's order.
 */
export function coverChecklist(items, docs) {
  const covering = (Array.isArray(docs) ? docs : []).filter(d => d && d.providedAs && d.name);
  return (Array.isArray(items) ? items : []).map(item => {
    const k = kindOf(item);
    const doc = k ? covering.find(d => kindOf(d.providedAs) === k) : null;
    return { item, providedBy: doc ? doc.name : null };
  });
}

// The line for an emailed list (no dashes).
export function coveredLine(c) {
  return c.providedBy ? `${c.item} (already provided: ${c.providedBy})` : c.item;
}
